import React from 'react'
import {
  Table,
  Tag,
  Tooltip,
  Typography,
  Space,
  Button,
  Modal,
  type TablePaginationConfig
} from 'antd'
import type { SorterResult, TableCurrentDataSource } from 'antd/es/table/interface'
import { EyeOutlined, FilterOutlined, InfoCircleOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { WebhookLog, State } from '../types'
import {
  formatDate,
  getStatusColor,
  getStatusText,
  formatEntity,
  truncateUrl,
  formatHeaders,
  formatQueryParams,
  getEntityOptions,
  getStatusOptions,
  getTokenValidOptions,
  getOperationOptions
} from '../utils'
import type { FilterValue } from 'antd/es/table/interface'

const { Text } = Typography

interface WebhookLogsTableProps {
  data: WebhookLog[]
  loading: boolean
  pagination: {
    current: number
    pageSize: number
    total: number
  }
  columnFilters: State['columnFilters']
  onPaginationChange: (page: number, pageSize: number) => void
  onColumnFiltersChange: (filters: State['columnFilters']) => void
}

export const WebhookLogsTable: React.FC<WebhookLogsTableProps> = ({
  data,
  loading,
  pagination,
  columnFilters,
  onPaginationChange,
  onColumnFiltersChange
}) => {
  const [detailsModal, setDetailsModal] = React.useState<{
    visible: boolean
    record?: WebhookLog
  }>({ visible: false })

  const showDetails = (record: WebhookLog) => {
    setDetailsModal({ visible: true, record })
  }

  const hideDetails = () => {
    setDetailsModal({ visible: false, record: undefined })
  }

  const columns: ColumnsType<WebhookLog> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: true
    },
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => <Text style={{ fontSize: '12px' }}>{formatDate(date)}</Text>,
      sorter: true
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      width: 200,
      render: (url: string) => (
        <Tooltip title={url}>
          <Text code style={{ fontSize: '11px' }}>
            {truncateUrl(url, 30)}
          </Text>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'response_status',
      key: 'response_status',
      width: 120,
      render: (status: number) => (
        <Tag color={getStatusColor(status)}>
          {status} {getStatusText(status)}
        </Tag>
      ),
      sorter: true,
      filters: getStatusOptions().map(option => ({
        text: option.label,
        value: option.value
      })),
      filteredValue: columnFilters.responseStatus?.map(String) || null,
      onFilter: () => true, // Filtering is handled by API
      filterIcon: () => (
        <FilterOutlined
          style={{ color: (columnFilters.responseStatus?.length || 0) > 0 ? '#1890ff' : undefined }}
        />
      )
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
      width: 100,
      render: (entity: string | null) => <Text>{formatEntity(entity)}</Text>,
      filters: getEntityOptions().map(option => ({
        text: option.label,
        value: option.value
      })),
      filteredValue: columnFilters.entity || null,
      onFilter: () => true, // Filtering is handled by API
      filterIcon: () => (
        <FilterOutlined
          style={{ color: (columnFilters.entity?.length || 0) > 0 ? '#1890ff' : undefined }}
        />
      )
    },
    {
      title: 'EOB',
      dataIndex: 'eob_number',
      key: 'eob_number',
      width: 120,
      render: (eob: string | null) => (
        <Text code style={{ fontSize: '11px' }}>
          {eob || '-'}
        </Text>
      )
    },
    {
      title: 'File',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 150,
      render: (filename: string | null) => (
        <Tooltip title={filename}>
          <Text style={{ fontSize: '11px' }}>{filename ? truncateUrl(filename, 20) : '-'}</Text>
        </Tooltip>
      )
    },
    {
      title: 'Token',
      dataIndex: 'token_valid',
      key: 'token_valid',
      width: 80,
      render: (valid: boolean) => (
        <Tag color={valid ? 'success' : 'error'}>{valid ? 'OK' : 'Fail'}</Tag>
      ),
      filters: getTokenValidOptions().map(option => ({
        text: option.label,
        value: option.value
      })),
      filteredValue: columnFilters.tokenValid?.map(String) || null,
      onFilter: () => true, // Filtering is handled by API
      filterIcon: () => (
        <FilterOutlined
          style={{ color: (columnFilters.tokenValid?.length || 0) > 0 ? '#1890ff' : undefined }}
        />
      )
    },
    {
      title: 'Operation',
      dataIndex: 'is_on_hold',
      key: 'is_on_hold',
      width: 100,
      render: (isOnHold: boolean | null) => {
        if (isOnHold === null) {
          return <Text>-</Text>
        }
        return <Tag color={isOnHold ? 'warning' : 'default'}>{isOnHold ? 'Hold' : 'Unhold'}</Tag>
      },
      filters: getOperationOptions().map(option => ({
        text: option.label,
        value: option.value
      })),
      filteredValue: columnFilters.isOnHold?.map(String) || null,
      onFilter: () => true, // Filtering is handled by API
      filterIcon: () => (
        <FilterOutlined
          style={{ color: (columnFilters.isOnHold?.length || 0) > 0 ? '#1890ff' : undefined }}
        />
      )
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 100,
      render: (error: string | null) => {
        if (!error) {
          return <Text>-</Text>
        }
        return (
          <Tooltip title={error}>
            <Tag color='error' icon={<InfoCircleOutlined />}>
              Error
            </Tag>
          </Tooltip>
        )
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type='link' size='small' icon={<EyeOutlined />} onClick={() => showDetails(record)}>
          Details
        </Button>
      )
    }
  ]

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    _sorter: SorterResult<WebhookLog> | SorterResult<WebhookLog>[],
    extra: TableCurrentDataSource<WebhookLog>
  ) => {
    if (extra.action === 'filter') {
      const newColumnFilters: State['columnFilters'] = {
        entity: (filters.entity as string[]) || [],
        responseStatus: filters.response_status?.map(Number) || [],
        tokenValid: filters.token_valid?.map(val => val === 'true') || [],
        isOnHold: filters.is_on_hold?.map(val => val === 'true') || []
      }
      onColumnFiltersChange(newColumnFilters)
    }
  }

  return (
    <>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey='id'
        size='small'
        scroll={{ x: 1400 }}
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (page: number, pageSize?: number) => {
            onPaginationChange(page, pageSize || pagination.pageSize)
          },
          onShowSizeChange: (current: number, size: number) => {
            onPaginationChange(current, size)
          }
        }}
      />

      <Modal
        title={`Webhook Log Details #${detailsModal.record?.id}`}
        open={detailsModal.visible}
        onCancel={hideDetails}
        footer={null}
        width={800}
      >
        {detailsModal.record && (
          <Space direction='vertical' style={{ width: '100%' }} size='middle'>
            <div>
              <Text strong>Basic Information:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <p>
                  <Text strong>URL:</Text> <Text code>{detailsModal.record.url}</Text>
                </p>
                <p>
                  <Text strong>Method:</Text> <Tag color='blue'>{detailsModal.record.method}</Tag>
                </p>
                <p>
                  <Text strong>Time:</Text> {formatDate(detailsModal.record.created_at)}
                </p>
                <p>
                  <Text strong>IP Address:</Text> <Text code>{detailsModal.record.ip_address}</Text>
                </p>
                <p>
                  <Text strong>User Agent:</Text>{' '}
                  <Text style={{ fontSize: '11px' }}>{detailsModal.record.user_agent || '-'}</Text>
                </p>
              </div>
            </div>

            <div>
              <Text strong>Query Parameters:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                  {formatQueryParams(detailsModal.record.query_params)}
                </Text>
              </div>
            </div>

            <div>
              <Text strong>Headers:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                  {formatHeaders(detailsModal.record.headers)}
                </Text>
              </div>
            </div>

            {detailsModal.record.error_message && (
              <div>
                <Text strong>Error:</Text>
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    background: '#fff2f0',
                    borderRadius: 4,
                    border: '1px solid #ffccc7'
                  }}
                >
                  <Text type='danger' style={{ fontSize: '12px' }}>
                    {detailsModal.record.error_message}
                  </Text>
                </div>
              </div>
            )}

            {detailsModal.record.eob_data && (
              <div>
                <Text strong>EOB Data:</Text>
                <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  <Text code style={{ whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                    {JSON.stringify(detailsModal.record.eob_data, null, 2)}
                  </Text>
                </div>
              </div>
            )}
          </Space>
        )}
      </Modal>
    </>
  )
}
